/**
 * "You have changes that aren't saved" warning for the content editor.
 *
 * The app uses <BrowserRouter>, where react-router's useBlocker is not
 * available, so the three ways out of a page are guarded here:
 *
 * - closing or reloading the tab: `beforeunload`;
 * - tapping a link (header tabs, "Back to the list") or a button marked
 *   `data-leaves-page` (Sign out): a capture-phase click listener on
 *   document, which runs before react-router's Link handler and React's own
 *   onClick, and cancels the click when she chooses to stay;
 * - the browser Back button or a phone's back swipe: a `popstate` listener
 *   registered when this module loads, so it runs before react-router's own
 *   listener (added when the router mounts). When she chooses to stay it
 *   stops the event, so the router never sees it, and steps history back to
 *   the editor's entry.
 *
 * @module content/leaveGuard
 */

import { useEffect } from 'react';

export const LEAVE_MESSAGE = 'You have changes that aren’t saved. Leave this page and lose them?';

/** Set while an editor has unsaved changes: { message, idx } (idx = react-router's history index). */
let active = null;
let ignoreNextPop = false;

const historyIndex = () => {
  const state = window.history.state;
  return state && typeof state.idx === 'number' ? state.idx : null;
};

function onPopState(event) {
  if (ignoreNextPop) {
    ignoreNextPop = false;
    event.stopImmediatePropagation();
    return;
  }
  if (!active) return;
  if (window.confirm(active.message)) {
    active = null;
    return;
  }
  event.stopImmediatePropagation();
  const now = historyIndex();
  const delta = active.idx !== null && now !== null ? active.idx - now : 1;
  if (delta !== 0) {
    ignoreNextPop = true;
    window.history.go(delta);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', onPopState, true);
}

const isPlainLeftClick = (event) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Warn before leaving while `dirty` is true.
 * @param {boolean} dirty
 * @param {string} [message]
 */
export function useUnsavedChangesWarning(dirty, message = LEAVE_MESSAGE) {
  useEffect(() => {
    if (!dirty) return undefined;
    const guard = { message, idx: historyIndex() };
    active = guard;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Older browsers need returnValue set to show their own prompt.
      event.returnValue = '';
    };

    const onClick = (event) => {
      if (event.defaultPrevented || !isPlainLeftClick(event)) return;
      const link = event.target instanceof Element ? event.target.closest('a[href], [data-leaves-page]') : null;
      if (!link || link.hasAttribute('download')) return;
      if (link.target && link.target !== '_self') return; // opens elsewhere; this page stays
      if (window.confirm(message)) {
        active = null;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      if (active === guard) active = null;
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, message]);
}
